"use client";

import { useRouter } from "@/i18n/routing";
import { ChevronDown } from "lucide-react";
import {
  LJ_SEARCH_SORTS,
  type LjSearchSort,
} from "@/fixtures/lj/search-fixtures";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";

/**
 * Result ordering, as a dropdown beside the count.
 *
 * QA: "for search result, add sorting dropdown: relevance/newest/oldest, add
 * this dropdown next to the 'XX RESULTS FOR “XXX”'". A native `<select>`
 * rather than a custom menu — it is one control, it is keyboard- and
 * screen-reader-complete for free, and on a phone it opens the platform's own
 * picker. The choice lands in the URL, so a sorted result page is linkable and
 * the back button restores it.
 */
export function SearchSort({
  query,
  value,
}: {
  query: string;
  value: LjSearchSort;
}) {
  const router = useRouter();

  return (
    <label className="inline-flex items-center gap-2">
      <span
        className="whitespace-nowrap"
        style={{ ...LJ_TYPE.eyebrow, color: LJ_COLORS.textSecondary }}
      >
        Sort by
      </span>
      <span className="relative inline-flex items-center">
        <select
          value={value}
          onChange={(e) =>
            router.push(
              `/lj/search?q=${encodeURIComponent(query)}&sort=${e.target.value}`,
            )
          }
          // No fill on hover — QA: "the sort dropdown, do not add hover
          // background colour effect". The border darkens instead, which is
          // enough to say the control is live without turning a menu trigger
          // into a pressed-looking plate.
          className="appearance-none border border-[color:var(--rest-border)] bg-transparent py-2 pl-3 pr-8 text-[color:var(--rest-fg)] outline-none transition-colors duration-150 hover:border-[color:var(--hover-border)] motion-reduce:transition-none"
          style={
            {
              ...LJ_TYPE.eyebrow,
              ["--rest-fg" as string]: LJ_COLORS.textPrimary,
              ["--rest-border" as string]: LJ_COLORS.dividerHair,
              ["--hover-border" as string]: LJ_COLORS.textPrimary,
            } as React.CSSProperties
          }
        >
          {LJ_SEARCH_SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-2 h-4 w-4"
          style={{ color: LJ_COLORS.textPrimary }}
          aria-hidden="true"
        />
      </span>
    </label>
  );
}
